import React, { useState } from 'react';
import { Box, Container, TextField, Button, Typography, Paper, Alert, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { endpoints } from '../../services/apiClient';
import carbonSpaceLogo from '../../assets/logo.png';
import mapSvg from '../../assets/map.svg';

const LoginContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100vh',
  backgroundColor: theme.palette.background.default,
}));

const LoginPaper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  borderRadius: (theme.shape.borderRadius as number) * 2,
  overflow: 'hidden',
  boxShadow: '0px 20px 40px rgba(0, 0, 0, 0.1)',
  maxWidth: 960,
  width: '100%',
}));

const FormSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: '50%',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    padding: theme.spacing(4),
  },
}));

const ImageSection = styled(Box)(({ theme }) => ({
  width: '50%',
  backgroundColor: theme.palette.accent.lightBlue,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.primary,
  padding: theme.spacing(6),
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const LogoImage = styled('img')(({ theme }) => ({
  width: 150,
  marginBottom: theme.spacing(2),
}));

const MapImage = styled('img')({
  maxWidth: '100%',
  maxHeight: '100%',
});

type ResetStep = 'validate-email' | 'request-code' | 'confirm';

interface ForgotPasswordViewProps {
  onBackToLogin: () => void;
}

const ForgotPasswordView: React.FC<ForgotPasswordViewProps> = ({ onBackToLogin }) => {
  const [step, setStep] = useState<ResetStep>('validate-email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password: string): boolean => {
    return password.length >= 8;
  };

  const handleValidateEmail = async () => {
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // First check if user exists using our backend endpoint
      const response = await endpoints.checkUserExists(email);

      if (!response.exists) {
        setError('No account found with this email address');
        return;
      }

      // If user exists, send reset password code
      await resetPassword({ username: email });
      setStep('request-code');
      setSuccess('Email verified! Verification code sent to your email');
    } catch (err: any) {
      console.error('Email validation error:', err);
      if (err.message?.includes('No account found')) {
        setError('No account found with this email address');
      } else if (err.name === 'LimitExceededException') {
        setError('Too many attempts. Please try again later');
      } else {
        setError(err.message || 'Failed to verify email address');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestCode = async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Send another verification code
      await resetPassword({ username: email });
      setSuccess('New verification code sent to your email');
    } catch (err: any) {
      console.error('Resend code error:', err);
      if (err.name === 'LimitExceededException') {
        setError('Too many attempts. Please try again later');
      } else {
        setError(err.message || 'Failed to send verification code');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmReset = async () => {
    if (!code || !newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (!validatePassword(newPassword)) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: newPassword,
      });
      setSuccess('Password reset successful! You can now sign in with your new password.');
      setTimeout(() => {
        onBackToLogin();
      }, 2000);
    } catch (err: any) {
      console.error('Confirm reset password error:', err);
      if (err.name === 'CodeMismatchException') {
        setError('Invalid verification code');
      } else if (err.name === 'ExpiredCodeException') {
        setError('Verification code has expired. Please request a new one');
      } else if (err.name === 'InvalidPasswordException') {
        setError('Password does not meet requirements');
      } else {
        setError(err.message || 'Failed to reset password');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      if (step === 'validate-email') {
        handleValidateEmail();
      } else if (step === 'request-code') {
        handleRequestCode();
      } else {
        handleConfirmReset();
      }
    }
  };

  const handleProceedToConfirm = () => {
    setStep('confirm');
    setError('');
    setSuccess('');
  };

  const handleBackToEmailValidation = () => {
    setStep('validate-email');
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
  };

  const handleBackToRequestCode = () => {
    setStep('request-code');
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
  };

  return (
    <LoginContainer maxWidth={false}>
      <LoginPaper>
        <FormSection>
          <LogoImage src={carbonSpaceLogo} alt="CarbonSpace Logo" />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }} gutterBottom>
            {step === 'validate-email' ? 'Reset Password' :
             step === 'request-code' ? 'Verification Code Sent' : 'Enter Verification Code'}
          </Typography>
          <Typography variant="body2" color="textSecondary" mb={4}>
            {step === 'validate-email'
              ? 'Enter your email address and we\'ll verify it exists'
              : step === 'request-code'
              ? 'A verification code has been sent to your email. Click "Enter Code" to proceed.'
              : 'Enter the verification code sent to your email and your new password'
            }
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {step === 'validate-email' ? (
            <>
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                margin="normal"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                autoFocus
              />

              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 2, py: 1.5, textTransform: 'none', fontSize: '1rem' }}
                onClick={handleValidateEmail}
                disabled={isLoading}
              >
                {isLoading ? 'Verifying Email...' : 'Verify Email'}
              </Button>
            </>
          ) : step === 'request-code' ? (
            <>
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                margin="normal"
                value={email}
                disabled
                sx={{ backgroundColor: '#f5f5f5' }}
              />

              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 2, py: 1.5, textTransform: 'none', fontSize: '1rem' }}
                onClick={handleProceedToConfirm}
                disabled={isLoading}
              >
                Enter Code
              </Button>

              <Button
                variant="text"
                fullWidth
                sx={{ mt: 1, textTransform: 'none' }}
                onClick={handleRequestCode}
                disabled={isLoading}
              >
                {isLoading ? 'Resending...' : 'Resend Code'}
              </Button>

              <Button
                variant="text"
                fullWidth
                sx={{ mt: 1, textTransform: 'none' }}
                onClick={handleBackToEmailValidation}
                disabled={isLoading}
              >
                Back to Email Entry
              </Button>
            </>
          ) : (
            <>
              <TextField
                label="Verification Code"
                variant="outlined"
                fullWidth
                margin="normal"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                placeholder="123456"
                autoFocus
                helperText="Enter the 6-digit code from your email"
              />

              <TextField
                label="New Password"
                type="password"
                variant="outlined"
                fullWidth
                margin="normal"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
                helperText="Minimum 8 characters"
              />

              <TextField
                label="Confirm New Password"
                type="password"
                variant="outlined"
                fullWidth
                margin="normal"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isLoading}
              />

              <Button
                variant="contained"
                color="primary"
                fullWidth
                sx={{ mt: 2, py: 1.5, textTransform: 'none', fontSize: '1rem' }}
                onClick={handleConfirmReset}
                disabled={isLoading}
              >
                {isLoading ? 'Resetting Password...' : 'Reset Password'}
              </Button>

              <Button
                variant="text"
                fullWidth
                sx={{ mt: 1, textTransform: 'none' }}
                onClick={handleBackToRequestCode}
                disabled={isLoading}
              >
                Back to Code Sent
              </Button>
            </>
          )}

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Link
              component="button"
              variant="body2"
              onClick={onBackToLogin}
              disabled={isLoading}
              sx={{ textDecoration: 'none' }}
            >
              Back to Sign In
            </Link>
          </Box>
        </FormSection>
        <ImageSection>
          <MapImage src={mapSvg} alt="Map Illustration" />
        </ImageSection>
      </LoginPaper>
    </LoginContainer>
  );
};

export default ForgotPasswordView;
